'use client';

import React from 'react';
import type { RenderComponent, SurfaceComponent } from '@macaron-a2ui/core/v0_8';
import { MacaronA2UISnapshotProvider } from './context';
import { MacaronA2UISurface } from './MacaronA2UISurface';

const SELF_CONTAINED_SURFACE_COMPONENTS = new Set(['Card', 'RollPickerCard']);
const CANVAS_SHADOW_BLEED_CLASSNAME = 'w-full min-w-0 -mx-2 px-2';
const FALLBACK_CARD_CLASSNAME = 'w-full md:w-fit md:min-w-[360px] md:max-w-full overflow-hidden rounded-2xl px-5 py-4';
const FALLBACK_CARD_STYLE: React.CSSProperties = {
  width: 'fit-content',
  minWidth: 360,
  maxWidth: '100%',
  padding: '1rem 1.25rem',
  backgroundColor: 'rgba(255, 255, 255, 0.72)',
  boxShadow: '0 18px 45px rgba(88, 62, 33, 0.14)',
  backdropFilter: 'blur(12px)',
};

export interface MacaronCanvasRendererProps {
  payload?: Record<string, unknown>;
  messages?: Record<string, unknown>[];
  renderComponent: RenderComponent;
  onAction?: (actionName: string, sourceComponentId: string, context?: Record<string, unknown> | null) => void;
  onLocalHandler?: (operation: string, sourceComponentId: string, value?: unknown) => void;
  fallbackCardClassName?: string;
  fallbackCardStyle?: React.CSSProperties;
}

function wrapMacaronSurface(renderedRoot: React.ReactNode, components: SurfaceComponent[], fallbackCardClassName = FALLBACK_CARD_CLASSNAME, fallbackCardStyle = FALLBACK_CARD_STYLE) {
  const hasExplicitCardSurface = components.some((item) => {
    if (!item?.component || typeof item.component !== 'object') return false;
    // Some widgets render their own full card shell even if the payload does not include a raw `Card` node.
    return Object.keys(item.component).some((type) => SELF_CONTAINED_SURFACE_COMPONENTS.has(type));
  });
  if (!hasExplicitCardSurface) {
    // Some A2UI payloads are just rows/columns/labels with no explicit Card nodes.
    // In chat they still need the same visual separation as card-based payloads, or the whole surface reads like plain prose.
    return (
      <div className={CANVAS_SHADOW_BLEED_CLASSNAME}>
        <div data-slot="macaron-canvas-surface-fallback-card" className={fallbackCardClassName} style={fallbackCardStyle}>
          {renderedRoot}
        </div>
      </div>
    );
  }
  return <div className={CANVAS_SHADOW_BLEED_CLASSNAME}>{renderedRoot}</div>;
}

export default function MacaronCanvasRenderer({ payload, messages, renderComponent, onAction, onLocalHandler, fallbackCardClassName, fallbackCardStyle }: MacaronCanvasRendererProps) {
  return (
    <MacaronA2UISnapshotProvider payload={payload} messages={messages} renderComponent={renderComponent} onAction={onAction} onLocalHandler={onLocalHandler}>
      <MacaronA2UISurface wrapRoot={(root, components) => wrapMacaronSurface(root, components, fallbackCardClassName, fallbackCardStyle)} />
    </MacaronA2UISnapshotProvider>
  );
}
